/**
 * Simple Music Player Context
 *
 * Simplified context that uses the useHowlerPlayer hook
 * Eliminates complex state management and race conditions
 * Redux is the single source of truth for playlist state
 * 
 * Cross-browser audio handling:
 * - Prefers HTML5 Audio on iOS for reliable lock-screen/background playback
 * - Uses HTML5 Audio on other platforms for better streaming support
 * - Provides proper mute/unmute functionality across all platforms
 * 
 * Media Session API integration:
 * - Coordinates with MediaSessionService for hardware media keys and Bluetooth controls
 * - Handles external pause/play events (AirPods removal, media notifications, etc.)
 * - Updates OS media controls with track metadata and playback state
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHowlerPlayer, UseHowlerPlayerOptions } from '@/app/hooks/useHowlerPlayer';
import { useLoading } from './LoadingContext';
import { useAppDispatch } from '@store/index';
import { Logger } from '@/app/utils/logger';
import { shouldUseWebAudioForMusic, isIOSDevice } from '@/app/utils/platform';
import { getMediaSessionService } from '@/app/services/audio/MediaSessionService';
import { useDeferredEffect } from '@/app/hooks/useDeferredEffect';
import { DEMO_PLAYBACK_START_EVENT } from '@/app/constants/demoEvents';
import {
  SimpleMusicPlayerContext,
  SimpleMusicPlayerContextValue,
} from './SimpleMusicPlayerContextCore';
import { PlayerState } from '@/types/AudioPlayer';
import {
  selectCurrentTrack,
  selectLoopMode,
  selectHasNextTrack,
  selectTotalTracks,
  nextTrack as nextTrackAction,
  previousTrack as previousTrackAction,
  setCurrentTrackIndex,
  fetchPlaylistFromR2,
  refreshPlaylistFromR2,
  setShuffle,
  setLoopMode,
} from '@store/musicPlaylist/musicPlaylistSlice';
import { getMusicVolume, setMusicVolume } from '@store/userPreferences/userPreferencesSlice';

type E2EMusicSnapshot = {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  title: string | null;
  artist: string | null;
  audioPaused: boolean | null;
  audioCurrentTime: number | null;
  html5AudioAvailable: boolean;
};

type E2EDebugApi = {
  getMusicSnapshot?: () => E2EMusicSnapshot;
};

type E2EWindow = Window & {
  __CHILL_AIRWAVES_E2E__?: E2EDebugApi;
};

const E2E_FORCE_HTML5_AUDIO_STORAGE_KEY = '__chill_airwaves_e2e_force_html5_audio';

interface SimpleMusicPlayerProviderProps {
  children: React.ReactNode;
  howlerOptions?: UseHowlerPlayerOptions;
}

const shouldHandleDemoPlaybackStart = import.meta.env.MODE === 'test'
  || __INCLUDE_DEMO__
  || import.meta.env.VITE_ENABLE_AUTOMATION === 'true'
  || import.meta.env.VITE_E2E_SETTINGS === 'true';

export const SimpleMusicPlayerProvider: React.FC<SimpleMusicPlayerProviderProps> = ({
  children,
  howlerOptions = {}
}) => {
  const dispatch = useAppDispatch();
  const { setMusicPlayerReady } = useLoading();

  // Prevent double initialization in StrictMode
  const hasInitializedRef = useRef(false);
  const playbackIntentRef = useRef(false);
  const playlistRefreshInFlightRef = useRef(false);
  const trackErrorRecoveryRef = useRef<{
    trackKey: string | null;
    refreshAttempted: boolean;
    skipAttempted: boolean;
  }>({
    trackKey: null,
    refreshAttempted: false,
    skipAttempted: false,
  });
  const trackLoadFailureWindowRef = useRef<{
    failedTrackKeys: Set<string>;
    circuitOpen: boolean;
  }>({
    failedTrackKeys: new Set<string>(),
    circuitOpen: false,
  });

  // Keep latest Howler callbacks in a ref so we can reference them from callbacks
  // defined before `useHowlerPlayer` executes (avoids TDZ issues and stale closures).
  // NOTE: always access via .current – the ref itself is stable and safe to use
  // inside effects with an empty (or minimal) dependency array.
  const howlerApiRef = useRef<{
    play: () => Promise<void>;
    pause: () => void;
    stop: () => void;
    seekTo: (time: number) => void;
  }>({
    play: async () => { },
    pause: () => { },
    stop: () => { },
    seekTo: () => { },
  });

  // Redux selectors
  const currentTrack = useSelector(selectCurrentTrack);
  const loopMode = useSelector(selectLoopMode);
  const hasNextTrack = useSelector(selectHasNextTrack);
  const totalTracks = useSelector(selectTotalTracks);
  const volume = useSelector(getMusicVolume);

  // Platform detection for audio routing
  const isIOS = isIOSDevice();
  const shouldUseWebAudio = shouldUseWebAudioForMusic();
  const isE2E = import.meta.env.VITE_E2E_SETTINGS === 'true';
  const forceHtml5InE2E = (() => {
    if (!isE2E) {
      return false;
    }

    if (import.meta.env.VITE_E2E_FORCE_HTML5_AUDIO === 'true') {
      return true;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.localStorage.getItem(E2E_FORCE_HTML5_AUDIO_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  })();
  const resolvedHtml5 = isE2E
    ? forceHtml5InE2E
    : (howlerOptions.html5 !== undefined ? howlerOptions.html5 : !shouldUseWebAudio);

  // Defer non-critical platform logging to reduce main-thread contention on startup
  useDeferredEffect(() => {
    Logger.debug('Music player platform detection', 'simple-music-player', {
      isIOS,
      shouldUseWebAudio,
      html5Option: howlerOptions.html5,
      forceHtml5InE2E,
      resolvedHtml5,
      willUseWebAudio: !resolvedHtml5,
    });
  }, [isIOS, resolvedHtml5, shouldUseWebAudio, howlerOptions.html5, forceHtml5InE2E], { minDelayMs: 50 });

  const handleHowlerEnd = useCallback((track: { title: string }) => {
    if (!playbackIntentRef.current) {
      Logger.warn('Ignoring onend without active playback intent', 'simple-music-player', {
        title: track.title,
      });
      return;
    }

    playbackIntentRef.current = false;

    Logger.debug('Track ended (onend), advancing playback', 'simple-music-player', {
      title: track.title,
      hasNextTrack,
      loopMode,
    });

    if (loopMode === 'one') {
      // Restart same track
      playbackIntentRef.current = true;
      howlerApiRef.current.seekTo(0);
      howlerApiRef.current.play().catch((error) => {
        playbackIntentRef.current = false;
        Logger.error('Failed to replay track (loop=one)', 'simple-music-player', {
          title: track.title,
          error: error instanceof Error ? error.message : String(error),
        });
      });
      return;
    }

    if (hasNextTrack || loopMode === 'all') {
      // Ensure the next loaded track auto-plays
      playbackIntentRef.current = true;
      setWasPlayingBeforeTrackChange(true);
      dispatch(nextTrackAction());
    }
  }, [dispatch, hasNextTrack, loopMode]);

  // Howler player hook with iOS-aware options
  // On iOS, prefer HTML5 Audio (html5: true) for reliable lock-screen/background playback

  // External event handlers for Media Session API coordination
  // These are called when audio is paused/played externally (e.g., AirPods removal, Bluetooth controls)
  const handleExternalPause = useCallback(() => {
    playbackIntentRef.current = false;
    Logger.info('External pause event detected (e.g., headphone disconnect)', 'simple-music-player');
    // Media Session Service handles coordinating ATC audio automatically
    const mediaService = getMediaSessionService();
    mediaService.handleExternalPause();
  }, []);

  const handleExternalPlay = useCallback(() => {
    playbackIntentRef.current = true;
    Logger.info('External play event detected (e.g., media notification)', 'simple-music-player');
    // Media Session Service handles coordinating ATC audio automatically
    const mediaService = getMediaSessionService();
    mediaService.handleExternalPlay();
  }, []);

  const {
    isPlaying,
    isLoading,
    hasError,
    errorMessage,
    currentTime,
    duration,
    play: howlerPlay,
    pause: howlerPause,
    stop: howlerStop,
    setVolume: howlerSetVolume,
    seekTo: howlerSeekTo,
    loadTrack,
    canPlay,
    isMuted,
    setMuted: howlerSetMuted,
    isUsingWebAudio,
    getAudioElement,
  } = useHowlerPlayer({
    ...howlerOptions,
    volume,
    // Auto-detect html5 mode based on platform if not explicitly set
    // iOS needs html5: false for volume control to work
    // E2E runs force Web Audio to avoid HTML5 audio pool exhaustion in Chromium.
    html5: resolvedHtml5,
    onEnd: handleHowlerEnd,
    shouldRecoverFromVisibilityPause: () => playbackIntentRef.current,
    // External event callbacks for Media Session coordination
    onExternalPause: handleExternalPause,
    onExternalPlay: handleExternalPlay,
  });

  useEffect(() => {
    if (!isE2E || typeof window === 'undefined') {
      return;
    }

    const e2eWindow = window as E2EWindow;
    const e2eApi: E2EDebugApi = e2eWindow.__CHILL_AIRWAVES_E2E__ || {};

    const snapshotProvider = (): E2EMusicSnapshot => ({
      isPlaying,
      currentTime,
      duration,
      title: currentTrack?.title || null,
      artist: currentTrack?.artist || null,
      audioPaused: getAudioElement()?.paused ?? null,
      audioCurrentTime: getAudioElement()?.currentTime ?? null,
      html5AudioAvailable: getAudioElement() !== null,
    });

    e2eApi.getMusicSnapshot = snapshotProvider;
    e2eWindow.__CHILL_AIRWAVES_E2E__ = e2eApi;

    return () => {
      if (!e2eWindow.__CHILL_AIRWAVES_E2E__) {
        return;
      }

      if (e2eWindow.__CHILL_AIRWAVES_E2E__.getMusicSnapshot === snapshotProvider) {
        delete e2eWindow.__CHILL_AIRWAVES_E2E__.getMusicSnapshot;
      }

      if (Object.keys(e2eWindow.__CHILL_AIRWAVES_E2E__).length === 0) {
        delete e2eWindow.__CHILL_AIRWAVES_E2E__;
      }
    };
  }, [isE2E, isPlaying, currentTime, duration, currentTrack?.title, currentTrack?.artist, getAudioElement]);

  // Keep the latest howler API available to callbacks.
  useEffect(() => {
    howlerApiRef.current = {
      play: howlerPlay,
      pause: howlerPause,
      stop: howlerStop,
      seekTo: howlerSeekTo,
    };
  }, [howlerPlay, howlerPause, howlerStop, howlerSeekTo]);

  // Capture latest playing state without re-triggering the track-load effect.
  const isPlayingRef = useRef(false);
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const [wasPlayingBeforeTrackChange, setWasPlayingBeforeTrackChange] = React.useState(false);
  const currentTrackKey = currentTrack ? `${currentTrack.id}:${currentTrack.audioUrl}` : null;

  const triggerPlaylistRefresh = useCallback(async (reason: 'track-error') => {
    if (playlistRefreshInFlightRef.current) {
      Logger.debug('Skipping playlist refresh because one is already in flight', 'simple-music-player', {
        reason,
      });
      return;
    }

    playlistRefreshInFlightRef.current = true;

    try {
      await dispatch(refreshPlaylistFromR2());
    } finally {
      playlistRefreshInFlightRef.current = false;
    }
  }, [dispatch]);

  useEffect(() => {
    const recovery = trackErrorRecoveryRef.current;
    const canArmRecoveryForTrack = recovery.trackKey === null || !hasError;

    if (recovery.trackKey !== currentTrackKey && canArmRecoveryForTrack) {
      trackErrorRecoveryRef.current = {
        trackKey: currentTrackKey,
        refreshAttempted: false,
        skipAttempted: false,
      };
    }
  }, [currentTrackKey, hasError]);

  useEffect(() => {
    if (!currentTrackKey || hasError || !canPlay) {
      return;
    }

    const failureWindow = trackLoadFailureWindowRef.current;
    if (failureWindow.failedTrackKeys.size === 0 && !failureWindow.circuitOpen) {
      return;
    }

    failureWindow.failedTrackKeys.clear();
    failureWindow.circuitOpen = false;

    Logger.info('Recovered from track load failures, closing outage circuit', 'simple-music-player', {
      trackId: currentTrack?.id,
      title: currentTrack?.title,
    });
  }, [canPlay, currentTrack?.id, currentTrack?.title, currentTrackKey, hasError]);

  useEffect(() => {
    if (!hasError || !currentTrack) {
      return;
    }

    const recovery = trackErrorRecoveryRef.current;
    if (recovery.trackKey !== currentTrackKey) {
      return;
    }

    const failureWindow = trackLoadFailureWindowRef.current;
    if (currentTrackKey) {
      failureWindow.failedTrackKeys.add(currentTrackKey);
    }

    if (totalTracks > 0 && failureWindow.failedTrackKeys.size >= totalTracks) {
      if (!failureWindow.circuitOpen) {
        failureWindow.circuitOpen = true;
        Logger.error('All tracks failed to load. Stopping auto-advance to avoid infinite retry loop', 'simple-music-player', {
          totalTracks,
        });
      }

      playbackIntentRef.current = false;
      setWasPlayingBeforeTrackChange(false);
      return;
    }

    if (!recovery.refreshAttempted) {
      recovery.refreshAttempted = true;
      void triggerPlaylistRefresh('track-error');
    }

    if (recovery.skipAttempted) {
      return;
    }

    recovery.skipAttempted = true;

    if (hasNextTrack) {
      const shouldContinuePlayback = playbackIntentRef.current || isPlayingRef.current || wasPlayingBeforeTrackChange;
      if (shouldContinuePlayback) {
        setWasPlayingBeforeTrackChange(true);
      }

      Logger.warn('Track failed to load, advancing to next track', 'simple-music-player', {
        trackId: currentTrack.id,
        title: currentTrack.title,
      });
      dispatch(nextTrackAction());
      return;
    }

    Logger.warn('Track unavailable and no next track to skip to', 'simple-music-player', {
      trackId: currentTrack.id,
      title: currentTrack.title,
    });
  }, [
    canPlay,
    currentTrack,
    currentTrackKey,
    dispatch,
    hasError,
    hasNextTrack,
    loopMode,
    totalTracks,
    triggerPlaylistRefresh,
    wasPlayingBeforeTrackChange,
  ]);

  // Track the last loaded track URL to prevent duplicate loads
  const lastLoadedTrackUrlRef = useRef<string | null>(null);

  // Volume control is available on all platforms:
  // - Desktop: HTMLMediaElement.volume is settable
  // - iOS: Routed through Web Audio GainNode workaround in useHowlerPlayer
  const canControlVolume = true;

  // Sync Redux volume into Howler
  useEffect(() => {
    const clampedVolume = Math.max(0, Math.min(100, volume));
    howlerSetVolume(clampedVolume);
  }, [volume, howlerSetVolume]);

  // Initialize playlist and settings on mount
  useEffect(() => {
    // Prevent double initialization in React StrictMode
    if (hasInitializedRef.current) {
      Logger.debug('Skipping duplicate music player initialization (StrictMode)', 'simple-music-player');
      return;
    }
    hasInitializedRef.current = true;

    Logger.info('Initializing music player', 'simple-music-player');

    // Fetch playlist from R2
    dispatch(fetchPlaylistFromR2());

    // Initialize shuffle from env var (default: enabled)
    const shuffleEnv = import.meta.env.VITE_MUSIC_SHUFFLE_ON_LOAD;
    const normalizedShuffleEnv = typeof shuffleEnv === 'string' ? shuffleEnv.trim().toLowerCase() : '';
    const shouldShuffleOnLoad = normalizedShuffleEnv ? normalizedShuffleEnv === 'true' : true;
    Logger.info(`${shouldShuffleOnLoad ? 'Enabling' : 'Disabling'} shuffle on load`, 'simple-music-player');
    dispatch(setShuffle(shouldShuffleOnLoad));

    // Initialize loop mode from env var
    const loopModeEnv = import.meta.env.VITE_MUSIC_LOOP_MODE as string | undefined;
    const validLoopModes = ['none', 'all', 'one'] as const;
    const defaultLoopMode = 'all';

    if (loopModeEnv && validLoopModes.includes(loopModeEnv as typeof validLoopModes[number])) {
      Logger.info(`Setting loop mode to: ${loopModeEnv}`, 'simple-music-player');
      dispatch(setLoopMode(loopModeEnv as 'none' | 'all' | 'one'));
    } else {
      if (loopModeEnv) {
        Logger.warn(`Invalid loop mode "${loopModeEnv}", using default: ${defaultLoopMode}`, 'simple-music-player');
      }
      dispatch(setLoopMode(defaultLoopMode));
    }

    // Notify loading context
    setMusicPlayerReady(true);
  }, [dispatch, setMusicPlayerReady]);

  // Initialize Media Session API for hardware media keys and Bluetooth controls.
  // This enables proper handling of AirPods disconnect/reconnect, media notifications, etc.
  //
  // IMPORTANT: this effect intentionally runs only ONCE on mount (deps = [dispatch]).
  // All Howler callbacks are accessed through howlerApiRef.current so they always
  // call the latest version without causing the effect to re-fire on every track
  // state change. Previously, passing howlerPlay/Pause/Stop/SeekTo as deps caused
  // the effect (and therefore mediaService.initialize) to be called 3-5 times per
  // track load because those useCallback refs change whenever Howler state updates.
  useEffect(() => {
    const mediaService = getMediaSessionService();

    // All Howler calls go through the ref \u2013 it is kept in sync by the effect above.
    const mediaSessionCallbacks = {
      onPlay: async () => {
        Logger.debug('Media Session: play action', 'simple-music-player');
        try {
          playbackIntentRef.current = true;
          await howlerApiRef.current.play();
        } catch (error) {
          playbackIntentRef.current = false;
          Logger.error('Media Session: failed to play', 'simple-music-player', {
            error: error instanceof Error ? error.message : String(error),
          });
        }
      },
      onPause: () => {
        Logger.debug('Media Session: pause action', 'simple-music-player');
        playbackIntentRef.current = false;
        howlerApiRef.current.pause();
      },
      onStop: () => {
        Logger.debug('Media Session: stop action', 'simple-music-player');
        playbackIntentRef.current = false;
        howlerApiRef.current.stop();
      },
      onNextTrack: () => {
        Logger.debug('Media Session: next track action', 'simple-music-player');
        dispatch(nextTrackAction());
      },
      onPreviousTrack: () => {
        Logger.debug('Media Session: previous track action', 'simple-music-player');
        // If more than 3 seconds into track, restart; otherwise go to previous
        if (currentTimeRef.current > 3) {
          howlerApiRef.current.seekTo(0);
        } else {
          dispatch(previousTrackAction());
        }
      },
      onSeekTo: (time: number) => {
        Logger.debug(`Media Session: seek to ${time}s`, 'simple-music-player');
        howlerApiRef.current.seekTo(time);
      },
    };

    mediaService.initialize(mediaSessionCallbacks);
    Logger.info('Media Session API initialized', 'simple-music-player');

    return () => {
      // Cleanup is handled by the singleton service
    };
    // dispatch is the only true dependency \u2013 it is stable (RTK guarantees this).
    // All other mutable values are accessed through stable refs.

  }, [dispatch]);

  // Keep MediaSessionService synchronized with the current music audio element.
  // This prevents stale references when Howler rotates internal HTMLAudioElements
  // across track loads and Safari resume flows.
  useEffect(() => {
    const mediaService = getMediaSessionService();
    const audioElement = canPlay ? getAudioElement() : null;
    mediaService.setMusicAudio(audioElement);
  }, [canPlay, currentTrack?.audioUrl, getAudioElement]);

  // Track current time in a ref for Media Session previous track logic
  const currentTimeRef = useRef(currentTime);
  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  // Update Media Session metadata when track changes
  useEffect(() => {
    if (currentTrack) {
      const mediaService = getMediaSessionService();
      mediaService.updateMetadata(currentTrack);
    }
  }, [currentTrack]);

  // Update Media Session playback state when playing/paused changes
  useEffect(() => {
    const mediaService = getMediaSessionService();
    mediaService.setPlaybackState(isPlaying ? 'playing' : 'paused');
  }, [isPlaying]);

  // Update Media Session position state periodically during playback
  useEffect(() => {
    if (isPlaying && duration > 0) {
      const mediaService = getMediaSessionService();
      mediaService.updatePositionState(currentTime, duration);
    }
  }, [isPlaying, currentTime, duration]);

  // Load track when Redux currentTrack changes
  useEffect(() => {
    if (currentTrack) {
      // Skip if this track is already requested/loaded.
      // Important: this prevents double-loads (and iOS WebAudio decode failures)
      // even if the effect fires twice.
      if (lastLoadedTrackUrlRef.current === currentTrack.audioUrl) {
        Logger.debug('Track load already in progress or loaded, skipping', 'simple-music-player', {
          title: currentTrack.title,
        });
        return;
      }

      // Remember if we were playing before the track change
      if (isPlayingRef.current) {
        setWasPlayingBeforeTrackChange(true);
        Logger.debug('Remembering playing state before track change', 'simple-music-player');
      }

      Logger.info(`Loading new track from Redux: ${currentTrack.title}`, 'simple-music-player');
      lastLoadedTrackUrlRef.current = currentTrack.audioUrl;

      loadTrack(currentTrack).catch((error) => {
        // If this request has been superseded by another track load, ignore it.
        // Important: otherwise a cancelled/stale load would clear the guard and allow a duplicate load.
        if (lastLoadedTrackUrlRef.current !== currentTrack.audioUrl) {
          Logger.debug('Ignoring load failure for superseded track', 'simple-music-player', {
            title: currentTrack.title,
            error: error instanceof Error ? error.message : String(error),
          });
          return;
        }

        Logger.error('Failed to load track from Redux', 'simple-music-player', {
          title: currentTrack.title,
          error: error instanceof Error ? error.message : String(error),
        });
      });
    }
  }, [currentTrack, loadTrack]);

  // Track end is handled via useHowlerPlayer({ onEnd })

  // Derive player state
  const playerState: PlayerState = React.useMemo(() => {
    if (isLoading) return PlayerState.BUFFERING;
    if (hasError) return PlayerState.ERROR;
    if (isPlaying) return PlayerState.PLAYING;
    if (canPlay) return PlayerState.CUED;
    return PlayerState.UNSTARTED;
  }, [isLoading, hasError, isPlaying, canPlay]);

  // Player controls
  const play = useCallback(async () => {
    try {
      await howlerPlay();
      playbackIntentRef.current = true;
    } catch (error) {
      playbackIntentRef.current = false;
      Logger.error('Failed to play track', 'simple-music-player', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [howlerPlay]);

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldHandleDemoPlaybackStart) {
      return;
    }

    const handleDemoPlaybackStart = () => {
      if (isPlaying) {
        return;
      }

      play().catch((error) => {
        Logger.warn('Demo playback start failed', 'simple-music-player', {
          error: error instanceof Error ? error.message : String(error),
        });
      });
    };

    window.addEventListener(DEMO_PLAYBACK_START_EVENT, handleDemoPlaybackStart);

    return () => {
      window.removeEventListener(DEMO_PLAYBACK_START_EVENT, handleDemoPlaybackStart);
    };
  }, [isPlaying, play]);

  // Auto-play when track loads if we were playing before
  useEffect(() => {
    if (canPlay && wasPlayingBeforeTrackChange && !isPlaying) {
      Logger.debug('Auto-playing new track (was playing before)', 'simple-music-player');
      setWasPlayingBeforeTrackChange(false);
      play().catch((error) => {
        Logger.error('Failed to auto-play new track', 'simple-music-player', {
          error: error.message,
        });
      });
    }
  }, [canPlay, wasPlayingBeforeTrackChange, isPlaying, play]);

  const pause = useCallback(() => {
    playbackIntentRef.current = false;
    howlerPause();
  }, [howlerPause]);

  const togglePlayPause = useCallback(async () => {
    if (isPlaying) {
      pause();
    } else {
      await play();
    }
  }, [isPlaying, pause, play]);

  const stop = useCallback(() => {
    playbackIntentRef.current = false;
    howlerStop();
  }, [howlerStop]);

  // Volume controls
  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(100, newVolume));
    if (clampedVolume === volume) {
      return;
    }
    dispatch(setMusicVolume(clampedVolume));
  }, [dispatch, volume]);

  // Mute controls
  const setMuted = useCallback((muted: boolean) => {
    howlerSetMuted(muted);
    Logger.debug(`Music ${muted ? 'muted' : 'unmuted'}`, 'simple-music-player');
  }, [howlerSetMuted]);

  const toggleMute = useCallback(() => {
    howlerSetMuted(!isMuted);
    Logger.debug(`Music ${!isMuted ? 'muted' : 'unmuted'}`, 'simple-music-player');
  }, [howlerSetMuted, isMuted]);

  // Navigation controls
  const nextTrack = useCallback(async () => {
    Logger.debug('Next track requested', 'simple-music-player');
    dispatch(nextTrackAction());
  }, [dispatch]);

  const previousTrack = useCallback(async () => {
    Logger.debug('Previous track requested', 'simple-music-player');

    // If more than 3 seconds into track, restart; otherwise go to previous
    if (currentTime > 3) {
      howlerSeekTo(0);
    } else {
      dispatch(previousTrackAction());
    }
  }, [dispatch, currentTime, howlerSeekTo]);

  const seekTo = useCallback((time: number) => {
    howlerSeekTo(time);
  }, [howlerSeekTo]);

  const seekToPercent = useCallback((percent: number) => {
    if (duration > 0) {
      const time = (percent / 100) * duration;
      howlerSeekTo(time);
    }
  }, [duration, howlerSeekTo]);

  const playTrackAtIndex = useCallback(async (index: number) => {
    Logger.debug(`Play track at index: ${index}`, 'simple-music-player');
    dispatch(setCurrentTrackIndex(index));
  }, [dispatch]);

  const retry = useCallback(() => {
    if (currentTrack) {
      Logger.info('Retrying track load', 'simple-music-player', {
        title: currentTrack.title,
      });

      const trackUrl = currentTrack.audioUrl;

      // Mark this URL as in-flight so the track-load effect doesn't trigger
      // a second parallel load while retry is already running.
      lastLoadedTrackUrlRef.current = trackUrl;

      loadTrack(currentTrack).catch((error) => {
        if (lastLoadedTrackUrlRef.current === trackUrl) {
          lastLoadedTrackUrlRef.current = null;
        }

        Logger.error('Retry failed', 'simple-music-player', {
          title: currentTrack.title,
          error: error.message,
        });
      });
    }
  }, [currentTrack, loadTrack]);

  // Computed values
  const isPaused = !isPlaying && canPlay;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const contextValue: SimpleMusicPlayerContextValue = {
    // Player state
    playerState,
    isPlaying,
    isPaused,
    isLoading,
    hasError,
    errorMessage,
    canPlay,

    // Track info
    currentTime,
    duration,
    progress,

    // Player controls
    play,
    pause,
    togglePlayPause,
    stop,

    // Volume controls
    setVolume,
    volume,

    // Mute controls
    isMuted,
    setMuted,
    toggleMute,

    // Navigation controls
    nextTrack,
    previousTrack,
    seekTo,
    seekToPercent,

    // Track selection
    playTrackAtIndex,

    // Utility
    retry,

    // Platform info
    isUsingWebAudio,
    canControlVolume,
  };

  return (
    <SimpleMusicPlayerContext.Provider value={contextValue}>
      {children}
    </SimpleMusicPlayerContext.Provider>
  );
};
